import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Users, ShieldCheck, Database, AlertTriangle, GitBranch, Columns3, Tag } from 'lucide-react';
import {
  LensPresetSection,
  useFieldPackLensPresets,
  useComputedLensPresets,
  type LensPresetGroup,
  type FieldPackLensPreset,
  type ComputedLensPreset,
  type ComputedLensAdapter,
} from '../../../core/assistant';
import {
  applyLens as applyComputedLens,
  clearLens as clearComputedLens,
  isLensActiveIn,
  type ProcessLensId,
} from '../scenarios';

/**
 * ProcessLensPresets — one-click chips that wire directly into the shared
 * viewRulesStore (no LLM round-trip), built on the shared
 * `LensPresetSection` + lens-preset hooks (same primitive as
 * NetworkLensPresets / OrgChartLensPresets).
 *
 * Process keeps its own category grouping (operational / governance /
 * compliance / data-flow) and basic/rich tiering; this file declares the
 * preset arrays and composes the shared chips into grouped rows. Labels and
 * descriptions are i18n keys resolved from the `process` namespace at render.
 */

type FieldGroup = 'operational' | 'governance' | 'compliance' | 'data-flow';

interface FieldPresetDef {
  id: string;
  labelKey: string;
  descriptionKey: string;
  icon: React.ElementType;
  kind: 'colorBy' | 'heatmap';
  field: string;
  group: FieldGroup;
  basicTier?: boolean;
  minColor?: string;
  maxColor?: string;
}

interface ComputedPresetDef {
  id: ProcessLensId;
  labelKey: string;
  descriptionKey: string;
  icon: React.ElementType;
  basicTier?: boolean;
}

type ProcessFieldPreset = FieldPackLensPreset & { group: FieldGroup; basicTier?: boolean };
type ProcessComputedPreset = ComputedLensPreset<ProcessLensId> & { basicTier?: boolean };

/**
 * Computed lens chips — derive their per-node value at runtime from the
 * diagram's geometry / structure rather than reading a pre-set attribute. The
 * `lane` lens is the cornerstone: it works on any diagram with a swimlane pool
 * table, no metadata required, so it's useful at both tiers.
 */
const COMPUTED_PRESET_DEFS: readonly ComputedPresetDef[] = [
  { id: 'lane', labelKey: 'lens.presets.lane.label', descriptionKey: 'lens.presets.lane.description', icon: Columns3, basicTier: true },
  { id: 'tag', labelKey: 'lens.presets.tag.label', descriptionKey: 'lens.presets.tag.description', icon: Tag, basicTier: true },
];

const FIELD_PRESET_DEFS: readonly FieldPresetDef[] = [
  { id: 'sla', labelKey: 'lens.presets.sla.label', descriptionKey: 'lens.presets.sla.description', icon: AlertTriangle, kind: 'heatmap', field: 'slaMs', group: 'operational', minColor: '#dcfce7', maxColor: '#dc2626' },
  { id: 'cycle-time', labelKey: 'lens.presets.cycleTime.label', descriptionKey: 'lens.presets.cycleTime.description', icon: Activity, kind: 'heatmap', field: 'cycleTimeMs', group: 'operational', minColor: '#dbeafe', maxColor: '#1e40af' },
  { id: 'owner', labelKey: 'lens.presets.owner.label', descriptionKey: 'lens.presets.owner.description', icon: Users, kind: 'colorBy', field: 'owner', group: 'governance' },
  { id: 'raci', labelKey: 'lens.presets.raci.label', descriptionKey: 'lens.presets.raci.description', icon: Users, kind: 'colorBy', field: 'raciStatus', group: 'governance' },
  { id: 'framework', labelKey: 'lens.presets.framework.label', descriptionKey: 'lens.presets.framework.description', icon: ShieldCheck, kind: 'colorBy', field: 'framework', group: 'compliance' },
  { id: 'control-status', labelKey: 'lens.presets.controlStatus.label', descriptionKey: 'lens.presets.controlStatus.description', icon: ShieldCheck, kind: 'colorBy', field: 'controlStatus', group: 'compliance' },
  { id: 'data-class', labelKey: 'lens.presets.dataClass.label', descriptionKey: 'lens.presets.dataClass.description', icon: Database, kind: 'colorBy', field: 'dataClass', group: 'data-flow' },
  { id: 'system', labelKey: 'lens.presets.system.label', descriptionKey: 'lens.presets.system.description', icon: GitBranch, kind: 'colorBy', field: 'systemId', group: 'data-flow' },
];

const GROUP_LABEL_KEYS: Record<FieldGroup, string> = {
  operational: 'lens.groups.operational',
  governance: 'lens.groups.governance',
  compliance: 'lens.groups.compliance',
  'data-flow': 'lens.groups.dataFlow',
};
const GROUP_ORDER: FieldGroup[] = ['operational', 'governance', 'compliance', 'data-flow'];

const computedAdapter: ComputedLensAdapter<ProcessLensId> = {
  apply: (id) => { applyComputedLens(id); },
  clear: (id) => { clearComputedLens(id); },
  isActive: (id, rules) => isLensActiveIn(id, rules),
};

interface ProcessLensPresetsProps {
  /** Basic shows only `basicTier` chips (Lane/Tag — geometry, no metadata). Rich shows all. */
  tier?: 'basic' | 'rich';
}

export const ProcessLensPresets: React.FC<ProcessLensPresetsProps> = ({ tier = 'rich' }) => {
  const { t } = useTranslation('process');

  const visiblePresets = useMemo<ProcessFieldPreset[]>(
    () =>
      (tier === 'basic' ? FIELD_PRESET_DEFS.filter((p) => p.basicTier) : FIELD_PRESET_DEFS).map((p) => ({
        id: p.id,
        label: t(p.labelKey),
        description: t(p.descriptionKey),
        icon: p.icon,
        kind: p.kind,
        field: p.field,
        minColor: p.minColor,
        maxColor: p.maxColor,
        group: p.group,
        basicTier: p.basicTier,
      })),
    [tier, t],
  );
  const visibleComputed = useMemo<ProcessComputedPreset[]>(
    () =>
      (tier === 'basic' ? COMPUTED_PRESET_DEFS.filter((p) => p.basicTier) : COMPUTED_PRESET_DEFS).map((p) => ({
        id: p.id,
        label: t(p.labelKey),
        description: t(p.descriptionKey),
        icon: p.icon,
        basicTier: p.basicTier,
      })),
    [tier, t],
  );

  const fieldPack = useFieldPackLensPresets(visiblePresets);
  const computed = useComputedLensPresets(visibleComputed, computedAdapter);

  // Field-pack chips are returned in `visiblePresets` order — partition them
  // back into their declared category groups for the grouped layout.
  const groups = useMemo<LensPresetGroup[]>(() => {
    const result: LensPresetGroup[] = [];
    // Computed (structural) row first — works on geometry alone, most accessible.
    if (computed.chips.length > 0) {
      result.push({ title: tier === 'rich' ? t('lens.groups.structural') : undefined, chips: computed.chips });
    }
    for (const group of GROUP_ORDER) {
      const chips = visiblePresets
        .map((preset, i) => ({ preset, chip: fieldPack.chips[i] }))
        .filter((entry) => entry.preset.group === group)
        .map((entry) => entry.chip);
      if (chips.length > 0) result.push({ title: t(GROUP_LABEL_KEYS[group]), chips });
    }
    return result;
  }, [computed.chips, fieldPack.chips, visiblePresets, tier, t]);

  return (
    <LensPresetSection
      groups={groups}
      onClearAll={() => {
        fieldPack.clearAll();
        computed.clearAll();
      }}
      helperText={tier === 'basic' ? t('lens.hintBasic') : t('lens.hintRich')}
    />
  );
};
